import { useState } from "react";
import { toast } from "sonner";
import { Search, Eye, Ban, DollarSign, MessageCircle, RotateCcw, Trash2 } from "lucide-react";
import { api, rd, inputCls, askPin, reminderWhatsAppUrl, paymentLabel } from "./posShared";
import PayModal from "./PayModal";
import ReturnModal from "./ReturnModal";

const STATUS_STYLE = {
  pagada: "bg-emerald-50 text-emerald-600",
  pendiente: "bg-amber-100 text-amber-700",
  anulada: "bg-red-100 text-red-600",
};

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Facturas({ invoices, customers = [], products = [], reload, onView, settings, isAdmin }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [payInvoice, setPayInvoice] = useState(null);
  const [returnInvoice, setReturnInvoice] = useState(null);

  const custMap = Object.fromEntries(customers.map((c) => [c.id, c]));
  const isOverdue = (i) => i.status === "pendiente" && i.balance > 0 && i.credit_due_date && i.credit_due_date < todayStr();

  const remind = (i) => {
    const c = custMap[i.customer_id];
    const num = c?.whatsapp || c?.phone || "";
    if (!num) return toast.error("El cliente no tiene WhatsApp/teléfono registrado");
    window.open(reminderWhatsAppUrl(i, settings, num), "_blank");
  };

  const filtered = invoices.filter((i) => {
    const okS = !status || i.status === status;
    const okQ = !q || [i.number, i.customer_name, i.ncf].some((f) => (f || "").toLowerCase().includes(q.toLowerCase()));
    return okS && okQ;
  });

  const voidInvoice = async (inv) => {
    const reason = window.prompt("Motivo de la anulación (obligatorio):");
    if (!reason) return;
    const pin = await askPin();
    try {
      await api.post(`/pos/invoices/${inv.id}/void`, { reason, pin });
      toast.success("Factura anulada");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`¿Eliminar PERMANENTEMENTE la factura ${inv.number}? Esta acción no se puede deshacer.`)) return;
    const pin = await askPin("Ingresa el PIN de administrador para eliminar la factura");
    if (!pin) return;
    try {
      await api.delete(`/pos/invoices/${inv.id}`, { params: { pin } });
      toast.success("Factura eliminada permanentemente");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div>
      <h1 className="font-serif text-3xl mb-6">Facturas</h1>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input data-testid="pos-inv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar # factura, cliente o NCF..." className={`${inputCls} pl-9`} />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} max-w-[180px]`}>
          <option value="">Todos los estados</option>
          <option value="pagada">Pagada</option>
          <option value="pendiente">Pendiente</option>
          <option value="anulada">Anulada</option>
        </select>
      </div>

      <div className="overflow-x-auto bg-white border border-neutral-200 rounded-xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 border-b border-neutral-100">
              <th className="px-4 py-3">Factura</th><th className="px-4 py-3">Fecha</th><th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">NCF</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Pago</th>
              <th className="px-4 py-3">Estado</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((i) => (
              <tr key={i.id} data-testid={`pos-invoice-row-${i.id}`} className="border-b border-neutral-50 hover:bg-neutral-50/60">
                <td className="px-4 py-3 font-medium">{i.number}</td>
                <td className="px-4 py-3 text-neutral-500 text-xs">{(i.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-3">{i.customer_name}</td>
                <td className="px-4 py-3 text-xs text-neutral-500">{i.ncf || "—"}</td>
                <td className="px-4 py-3 text-right font-medium">{rd(i.total)}</td>
                <td className="px-4 py-3 text-neutral-600">{paymentLabel(i.payment_method)}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs capitalize ${STATUS_STYLE[i.status]}`}>{i.status}</span>{isOverdue(i) && <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-600">Vencida</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2 justify-end">
                    <button data-testid={`pos-view-invoice-${i.id}`} onClick={() => onView(i)} className="p-1.5 border border-neutral-200 rounded-lg hover:border-espresso"><Eye size={14} /></button>
                    {i.status !== "anulada" && i.balance > 0 && (
                      <>
                        <button data-testid={`pos-remind-invoice-${i.id}`} onClick={() => remind(i)} title="Recordar cobro por WhatsApp" className="p-1.5 border border-neutral-200 rounded-lg text-rose hover:border-rose"><MessageCircle size={14} /></button>
                        <button data-testid={`pos-pay-invoice-${i.id}`} onClick={() => setPayInvoice(i)} title="Registrar pago" className="p-1.5 border border-neutral-200 rounded-lg text-emerald-600 hover:border-emerald-400"><DollarSign size={14} /></button>
                      </>
                    )}
                    {i.status !== "anulada" && (
                      <button data-testid={`pos-return-invoice-${i.id}`} onClick={() => setReturnInvoice(i)} title="Devolución" className="p-1.5 border border-neutral-200 rounded-lg text-copper hover:border-copper"><RotateCcw size={14} /></button>
                    )}
                    {i.status !== "anulada" && (
                      <button data-testid={`pos-void-invoice-${i.id}`} onClick={() => voidInvoice(i)} className="p-1.5 border border-neutral-200 rounded-lg text-neutral-400 hover:text-red-500 hover:border-red-300"><Ban size={14} /></button>
                    )}
                    {i.status === "anulada" && isAdmin && (
                      <button data-testid={`pos-delete-invoice-${i.id}`} onClick={() => deleteInvoice(i)} title="Eliminar permanentemente" className="p-1.5 border border-neutral-200 rounded-lg text-red-400 hover:text-red-600 hover:border-red-300"><Trash2 size={14} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-neutral-400">Sin facturas.</td></tr>}
          </tbody>
        </table>
      </div>

      {payInvoice && <PayModal invoice={payInvoice} onClose={() => setPayInvoice(null)} onPaid={() => reload()} />}
      {returnInvoice && <ReturnModal invoice={returnInvoice} products={products} onClose={() => setReturnInvoice(null)} onDone={() => reload()} />}
    </div>
  );
}
